import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

/**
 * Guided tours for new users. Steps point at elements marked with data-tour="…";
 * steps whose element is not on screen (e.g. filters on a phone) are skipped.
 * Plain language on purpose: many users are not technical.
 */
const KEY = (name) => `toxy.tour.${name}.v1`;

export function tourSeen(name) {
  try {
    return localStorage.getItem(KEY(name)) === 'done';
  } catch {
    return true; // storage blocked: never auto-start, the sidebar button still works
  }
}

function markSeen(name) {
  try {
    localStorage.setItem(KEY(name), 'done');
  } catch {
    /* ignore */
  }
}

export const SUBSTITUTE_TOUR_EXAMPLE = 'Big rattan pendant for a beach bar, natural look, 6 pieces';

const TOURS = {
  explore: [
    {
      popover: {
        title: 'Welcome to Toxy',
        description:
          'Our warehouse holds about <b>€600k of old stock that does not sell</b>. That is money stuck on a shelf, and space we pay for.<br><br>The goal of Toxy is simple: <b>old stock first</b>. Before we order something new for a client, we check what we already have, and we get rid of it together. For B2B and projects: offer these products in your projects.<br><br>First 3 quick ideas, then a short walk through the app.',
      },
    },
    {
      popover: {
        title: '1 · What counts as old stock?',
        description:
          'Only products we have in stock, in three groups:<br><br><b>Toxic</b>: in our warehouse for a long time and sells far too slowly (e.g. 1 year old and more than 6 months of stock left).<br><b>Watchlist</b>: not a problem yet, but heading that way.<br><b>Not on our website</b>: in stock, but customers cannot find it online.<br><br><span style="color:#64748b">Second-chance items and packaging are left out.</span>',
      },
    },
    {
      popover: {
        title: '2 · Every product gets three labels',
        description:
          '<b>Why is it stuck?</b> e.g. <span style="display:inline-block;margin:2px 2px 0 0;padding:1px 6px;border-radius:6px;font-size:11px;font-weight:500;background:#f1f5f9">Hardly anyone sees it</span><span style="display:inline-block;margin:2px 2px 0 0;padding:1px 6px;border-radius:6px;font-size:11px;font-weight:500;background:#f1f5f9">Many views, no sales</span><span style="display:inline-block;margin:2px 2px 0 0;padding:1px 6px;border-radius:6px;font-size:11px;font-weight:500;background:#f1f5f9">Only sold to businesses</span><br><br><b>Way out</b>: the most realistic way to sell it, e.g. <span style="display:inline-block;margin:2px 2px 0 0;padding:1px 6px;border-radius:6px;font-size:11px;font-weight:500;background:#ccfbf1">B2B project lot</span><span style="display:inline-block;margin:2px 2px 0 0;padding:1px 6px;border-radius:6px;font-size:11px;font-weight:500;background:#ede9fe">Signature</span><span style="display:inline-block;margin:2px 2px 0 0;padding:1px 6px;border-radius:6px;font-size:11px;font-weight:500;background:#dbeafe">Put back online</span><br><br><b>Priority</b>: what to do first <span style="display:inline-block;margin:2px 2px 0 0;padding:1px 6px;border-radius:6px;font-size:11px;font-weight:500;background:#fee2e2">Act now</span><span style="display:inline-block;margin:2px 2px 0 0;padding:1px 6px;border-radius:6px;font-size:11px;font-weight:500;background:#fef3c7">This quarter</span><span style="display:inline-block;margin:2px 2px 0 0;padding:1px 6px;border-radius:6px;font-size:11px;font-weight:500;background:#f1f5f9">Batch it</span><br><br>Hover a label on any card to read why, with the product’s own numbers.',
      },
    },
    {
      popover: {
        title: '3 · Where you come in',
        description:
          'About €130k of it are <b>B2B project lots</b>: 4 or more identical pieces, enough to furnish a hotel, office or restaurant. In stock means it ships today, and every product shows its <b>minimum price</b> (the lowest price without a loss), so you know your room to negotiate.',
      },
    },
    {
      element: '[data-tour="nav-/guide"]',
      popover: {
        title: 'Everything explained',
        description: 'The <b>How it works</b> page explains every label and number, in plain words and with the exact rules. Now let’s look at the app.',
        side: 'right',
      },
    },
    {
      element: '[data-tour="search-modes"]',
      popover: {
        title: 'Three ways to search',
        description:
          '<b>Search</b>: type words, a brand, an EAN or a vendor code.<br><b>Ask AI</b>: ask in your own words, e.g. “10 brass pendants for a hotel under €300”.<br><b>Visual</b>: drop a photo and find products that look like it.',
      },
    },
    {
      element: '[data-tour="search-input"]',
      popover: { title: 'Type and press Enter', description: 'Tip: press <kbd>/</kbd> anywhere on the page to jump straight to the search box.' },
    },
    {
      element: '[data-tour="quick-views"]',
      popover: { title: 'Quick views', description: 'One click for the most common questions: B2B lots of 4+ pieces, signature pieces, items to act on now, products we can put back online…' },
    },
    {
      element: '[data-tour="filters"]',
      popover: {
        title: 'Filters',
        description:
          'Narrow down by why it is stuck, the way out, priority, brand, colour, material… Click the <b>ⓘ</b> next to a heading to see what every option means, in plain words or with the exact rules.',
        side: 'right',
      },
    },
    {
      element: '[data-tour="results-header"]',
      popover: { title: 'Your results', description: 'How many products match and how much stock value that is. Change the order (e.g. most sold, oldest) or switch between cards and a table.' },
    },
    {
      element: '[data-tour="card"]',
      popover: {
        title: 'Reading a product card',
        description:
          '<b>Price</b>: red when already discounted, with the RRP crossed out.<br><b>Chips</b>: the way out (how to sell it) and why it is stuck.<br><b>Stock · Sold 6/12M</b>: how many we have and how fast it sells.<br><b>Cost · Min.</b>: purchase price and the lowest price without a loss.<br>Hover anything for an explanation, and <b>click the card</b> for photos, price history, live stock, product codes and similar items.',
        side: 'right',
      },
    },
    {
      element: '[data-tour="export"]',
      popover: { title: 'Export', description: 'Download the products you are looking at as a CSV file (opens in Excel).' },
    },
    {
      element: '[data-tour="nav-/shortlists"]',
      popover: { title: 'Shortlists', description: 'Collect products for a project or an idea, set quantities, check live stock in Odoo and export.', side: 'right' },
    },
    {
      element: '[data-tour="nav-/overview"]',
      popover: { title: 'Overview', description: 'Where the money is stuck, by reason, way out, priority and brand.', side: 'right' },
    },
    {
      element: '[data-tour="nav-/substitute"]',
      continueTo: 'substitute',
      popover: {
        title: 'Next: Substitute',
        description:
          'The most useful page for projects: a client asks for something, and Toxy finds what we already have in stock. Want to see it in action? (You can replay any tour from <b>Take the tour</b> at the bottom of the sidebar.)',
        side: 'right',
        nextBtnText: 'Show me Substitute',
      },
    },
  ],
  substitute: [
    {
      popover: {
        title: 'Substitute: old stock first',
        description:
          'A client asks for a product? Before ordering it, check here whether we already have something similar in stock. It ships today, and old stock finally leaves the warehouse. We loaded an example request so you can see how it works.',
      },
    },
    {
      element: '[data-tour="sub-sources"]',
      popover: {
        title: 'Start from what the client gave you',
        description:
          'A <b>photo</b> (a picture, a screenshot, a mood board), a <b>description</b> of any product, even one we do not sell, or a product from <b>our catalogue</b>.',
      },
    },
    {
      element: '[data-tour="sub-input"]',
      popover: {
        title: 'Drop, paste or type',
        description:
          'Here we typed: “Big rattan pendant for a beach bar, natural look, 6 pieces”. With a photo, you simply drop it here or paste a screenshot.',
      },
    },
    {
      element: '[data-tour="sub-understood"]',
      popover: {
        title: 'What Toxy understood',
        description: 'The AI turns the request into details: type, colour, material, size, price and how many pieces are needed. Toxy matches on these and on the look.',
        side: 'right',
      },
    },
    {
      element: '[data-tour="sub-card"]',
      popover: {
        title: 'A candidate from our stock',
        description:
          'Each card shows <b>why it matches</b> (the small chips: same type, same colour, enough pieces…), the price, stock and the minimum price. Too few pieces? It says “only 2 pcs (need 6)”. Click the card for all details.',
        side: 'left',
      },
    },
    {
      element: '[data-tour="sub-controls"]',
      popover: {
        title: 'Fine-tune',
        description: 'Match more on <b>looks</b>, <b>brand</b> or <b>price</b>, widen the price band, or set how many pieces you need.',
        side: 'right',
      },
    },
    {
      element: '[data-tour="sub-filters"]',
      popover: {
        title: 'Filters',
        description: 'The same filters as Explore: only B2B project lots, a brand, a colour, at least 6 pieces, a maximum purchase price… The numbers show how many candidates each option leaves.',
        side: 'right',
      },
    },
    {
      popover: {
        title: 'Let AI pick, and write to the client',
        description:
          'Tick <b>Candidate</b> on up to 12 cards and press <b>AI pick</b> at the bottom. The AI chooses the best options, explains why, and writes a short message you can copy into your email. Add the picks to a project <b>shortlist</b> in one click. Let’s get rid of that old stock together!',
      },
    },
  ],
};

export function startTour(name, { onContinue } = {}) {
  let d;
  const steps = (TOURS[name] || [])
    .filter((s) => !s.element || document.querySelector(s.element))
    .map((s) => {
      const { continueTo, ...step } = s;
      if (!continueTo || !onContinue) return { ...step, popover: { ...step.popover, nextBtnText: undefined } };
      // last step offers the next tour: its button ends this one and opens the next page
      return { ...step, popover: { ...step.popover, onNextClick: () => { d.destroy(); onContinue(continueTo); } } };
    });
  if (!steps.length) return;
  d = driver({
    showProgress: true,
    progressText: '{{current}} / {{total}}',
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    popoverClass: 'toxy-tour',
    stagePadding: 6,
    stageRadius: 10,
    overlayOpacity: 0.45,
    smoothScroll: true,
    steps,
    onDestroyed: () => markSeen(name),
  });
  d.drive();
}

/** Start a tour once, the first time a user reaches a page. */
export function startTourOnce(name, delay = 700, opts = {}) {
  if (tourSeen(name)) return () => {};
  const t = setTimeout(() => startTour(name, opts), delay);
  return () => clearTimeout(t);
}
